/**
 * useLabel — 경량 i18n 라벨 스토어 (web-admin/모바일과 동일 백엔드 `/api/v1/labels` 사용).
 *
 * 외부 i18n 라이브러리(i18next 등) 없이 모듈 스코프 가변 맵 + 간단한 구독(subscribe) 패턴으로
 * 구현한다. 호출부 시그니처는 t(key, fallback?) 로 기존과 동일하며, DB 시드 전에는 fallback
 * (한국어 하드코딩)을 그대로 반환하므로 안전하다.
 *
 * - loadLabels(locale): GET /api/v1/labels?locale= → 맵에 적재 + localStorage 저장(실패 허용).
 * - useLabel(): { t, locale, setLocale } 반환. setLocale 은 로드 후 전 구독자 리렌더.
 * - getLocale(): 현재 로캘 getter.
 */
import { useEffect, useState } from 'react'
import { api } from '@/api/client'

const STORAGE_KEY = 'fw.simLocale'
export const SUPPORTED_LOCALES = ['ko', 'en', 'vi'] as const
export type SimLocale = (typeof SUPPORTED_LOCALES)[number]

const readInitialLocale = (): string => {
  try {
    return localStorage.getItem(STORAGE_KEY) || 'ko'
  } catch {
    return 'ko'
  }
}

// 모듈 스코프 가변 상태 (단일 인스턴스 공유).
let labels: Record<string, string> = {}
let currentLocale: string = readInitialLocale()

// 구독자(컴포넌트 forceUpdate 콜백) 집합 — locale/labels 변경 시 일괄 호출.
const subscribers = new Set<() => void>()
const notify = () => subscribers.forEach((fn) => fn())

/** 현재 로캘 getter. */
export function getLocale(): string {
  return currentLocale
}

/**
 * 라벨 번들 로드 — 백엔드 응답 `{success, data:{KEY:value}}` 의 data(평면 맵)를 적재한다.
 * 네트워크/인증 실패는 조용히 흡수(호출부 fallback 으로 동작 유지).
 */
export async function loadLabels(locale: string): Promise<void> {
  currentLocale = locale
  try {
    localStorage.setItem(STORAGE_KEY, locale)
  } catch {
    /* localStorage 불가 — 무시 */
  }
  try {
    const res = await api.get('/api/v1/labels', { params: { locale } })
    const flat = (res?.data?.data ?? {}) as Record<string, string>
    labels = flat
  } catch {
    // 라벨 로드 실패 — 기존 맵 유지(또는 빈 맵) → fallback 문자열 사용
  }
  notify()
}

/**
 * useLabel — 라벨 훅. locale 변경 시 자동 리렌더(구독 등록)된다.
 */
export function useLabel() {
  const [, forceRender] = useState(0)

  useEffect(() => {
    const cb = () => forceRender((n) => n + 1)
    subscribers.add(cb)
    return () => {
      subscribers.delete(cb)
    }
  }, [])

  const t = (key: string, fallback?: string): string => labels[key] ?? fallback ?? key

  const setLocale = async (loc: string): Promise<void> => {
    await loadLabels(loc)
  }

  return { t, locale: currentLocale, setLocale }
}
